import { useState, useCallback } from "react";
import { getCsrfHeaders, fetchFreshCsrfToken } from "@/lib/queryClient";

interface UploadMetadata {
  name: string;
  size: number;
  contentType: string;
}

interface UploadResponse {
  objectPath: string;
  metadata: UploadMetadata;
}

interface UseUploadOptions {
  onSuccess?: (response: UploadResponse) => void;
  onError?: (error: Error) => void;
  allowedTypes?: string[];
}

const DEFAULT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function getReadableFileTypes(types: string[]): string {
  const typeMap: Record<string, string> = {
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WebP',
    'image/heic': 'HEIC',
    'image/heif': 'HEIF',
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'text/plain': 'TXT',
    'text/csv': 'CSV',
  };

  const readable = types.map(t => typeMap[t] || t).filter(Boolean);
  return readable.join(', ');
}

/**
 * React hook for handling file uploads via the server-side proxy endpoint.
 * All uploads are routed through /api/uploads/proxy where the file is
 * fully validated server-side (magic bytes, extension, size) before storage.
 */
export function useUpload(options: UseUploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState(0);

  /**
   * Validate a file before uploading (client-side pre-check only).
   * Server-side validation is the authoritative gate.
   */
  const validateFile = useCallback((file: File): string | null => {
    const allowedTypes = options.allowedTypes || DEFAULT_ALLOWED_TYPES;

    if (file.size > MAX_FILE_SIZE) {
      return `File is too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }

    const fileType = file.type || '';
    // If the browser cannot determine a MIME type (e.g. HEIC on some platforms),
    // skip client-side type validation and let the server be the authoritative gate.
    if (fileType && !allowedTypes.includes(fileType)) {
      const readableTypes = getReadableFileTypes(allowedTypes);
      return `File type "${file.type}" is not supported. Allowed types: ${readableTypes}`;
    }

    return null;
  }, [options.allowedTypes]);

  /**
   * Upload a file via the proxy endpoint.
   * The file passes through the server for full validation before reaching storage.
   */
  const uploadFile = useCallback(
    async (file: File): Promise<UploadResponse | null> => {
      setIsUploading(true);
      setError(null);
      setProgress(0);

      try {
        const validationError = validateFile(file);
        if (validationError) {
          throw new Error(validationError);
        }

        setProgress(30);

        const formData = new FormData();
        formData.append('file', file);

        const makeUploadRequest = () =>
          fetch("/api/uploads/proxy", {
            method: "POST",
            headers: {
              ...getCsrfHeaders("POST"),
            },
            body: formData,
          });

        let response = await makeUploadRequest();

        // If the CSRF token is stale (e.g. after session regeneration on login),
        // fetch a fresh token and retry exactly once before surfacing the error.
        if (response.status === 403) {
          try {
            const clone = response.clone();
            const parsed = await clone.json().catch(() => ({}));
            if (parsed?.message === "invalid csrf token") {
              await fetchFreshCsrfToken();
              response = await makeUploadRequest();
            }
          } catch {
            // If we can't parse the 403 body, fall through and let the
            // normal error handling report it.
          }
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to upload file");
        }

        const uploadResponse: UploadResponse = await response.json();

        setProgress(100);
        options.onSuccess?.(uploadResponse);
        return uploadResponse;
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error("Upload failed");
        setError(uploadError);
        options.onError?.(uploadError);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, options]
  );

  return {
    uploadFile,
    isUploading,
    error,
    progress,
  };
}
